import {
  Badge,
  Pill,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import { buildSubscriptions } from '@utils/subscriptions';
import { isFuture, isValid } from 'date-fns';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { useContext } from 'react';

import { csrClient } from '../../apollo/api/avantArteClient';
import { AuthEntryPoint } from '../../features/auth/types';
import { useAuthenticate } from '../../hooks/useAuthenticate';
import { useIsPreOrder } from '../../hooks/useIsPreOrder';
import { AccountContext } from '../../providers/AccountProvider';
import { PageName } from '../../types/Analytics';
import {
  GeneratedGetDrawByProductIDsQuery,
  queryGetProducts,
} from '../../types/generated';
import { TrackProductClicked } from '../../utils/analytics';
import {
  renderAugmentedArtistsNames,
  renderShopifyArtistName,
} from '../../utils/artists';
import {
  isAuctionRelease,
  isAvailable,
  isBlockchainAuctionRelease,
  isDrawRelease,
  isPrivateRelease,
  isSoldOut,
  isUpcoming,
} from '../../utils/product';
import { productUrl } from '../../utils/productUrl';
import { Connect } from '../Auction/Manifold/widgets/Connect';
import { Price as AuctionPrice } from '../Auction/Manifold/widgets/Price';
import ProductDate from '../Containers/ProductDate';
import { Image } from '../Image';
import { ProductPrice } from '../ProductPrice/ProductPrice';
import SubscribeBell from '../SubscribeBell/SubscribeBell';
import { getProductTilePill, getProductTileStatus } from './utils/status';
import { showPhoneStep } from '../../helpers/phone';
import { useAuth } from '../../providers';

const getProductStatus = (product: Product) => {
  if (isSoldOut(product)) return 'SOLD_OUT';
  if (isPrivateRelease(product)) return 'ENQUIRY';
  if (isUpcoming(product)) return 'COMING_SOON';
  if (!isAvailable(product)) return 'UNAVAILABLE';
  return 'AVAILABLE';
};

const Price = ({
  draw,
  product,
}: {
  draw?: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'][number];
  product: MultiCurrencyProduct;
}) => {
  const { t } = useTranslation('product');

  const isPriceVisible =
    isAuctionRelease(product.releaseType) ||
    product.cryptoPrice ||
    Number(product.price) > 0;

  if (!isPriceVisible) return null;

  const isPriceRange =
    product.productType === 'Collection' && !product.samePrice;
  const available = isAvailable(product);
  const soldOut = isSoldOut(product);

  if (isBlockchainAuctionRelease(product)) {
    if (!available && product.auctionFloorPrice) {
      return soldOut
        ? product.auctionFloorPrice
        : `${t('auction.from')} ${product.auctionFloorPrice}`;
    }

    if (available && product.manifoldListingId) {
      return (
        <span className="product-tile">
          <Connect className="hidden" />
          <AuctionPrice
            defaultValue={product.auctionFloorPrice}
            isRanked={product.releaseType === 'nft_ranked_auction'}
            listingId={product.manifoldListingId.toString()}
            prefix={t('auction.from')}
          />
        </span>
      );
    }
  }

  if (
    isAuctionRelease(product.releaseType) &&
    draw?.auctionReservePrice &&
    !soldOut
  ) {
    return null;
  }

  if (product.cryptoPrice) {
    return product.cryptoPrice;
  }

  return (
    <ProductPrice
      prefix={isPriceRange ? t('from') : undefined}
      price={Number(product.price)}
    />
  );
};

const ProductTile = ({
  analytics,
  draw,
  eagerLoading = false,
  isSubscribed,
  onSubscribe,
  product,
}: {
  onSubscribe: () => void;
  eagerLoading?: boolean;
  isSubscribed: boolean;
  product: Product;
  draw?: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'][number];
  analytics?: Analytics;
}) => {
  const { t } = useTranslation(['product', 'authentication']);
  const { user } = useAuth();
  const href = productUrl(product.full_slug!, product.productType);
  const pid = product.id.split('/').pop();
  const isPreOrder = useIsPreOrder(draw);
  const { onboarding } = useContext(AccountContext);

  const { authenticateAndSubscribe } = useAuthenticate();

  const isUpcomingProduct = isUpcoming(product);
  const phoneStep = showPhoneStep(user) && isUpcomingProduct;

  const handleClick = () => {
    if (!analytics) return;

    TrackProductClicked({
      page: analytics.page as PageName,
      position: analytics.position,
      context: analytics.context,
      id: product.id,
      productTitle: product.title,
      artist: product.artist,
      productType: product.productType,
      releaseDate: product.launchDate!,
      productUrl: href,
      price: parseInt(product.price, 10),
      currencyCode: product.currencyCode,
      editionSize: product.editionSize,
      ProductAvailibity: product.availability,
    });
  };

  const onClickSubscribe = async () => {
    try {
      const prods = await queryGetProducts(csrClient, {
        shopifyIDs: [product.id],
      });

      const artists = prods.data?.getProducts[0]?.artists;
      const variant = prods.data?.getProducts[0]?.variants[0];

      const subs = buildSubscriptions({
        includeGeneral: true,
        artists: artists.map((a) => ({ name: a.name, internalId: a.id })),
        productId: shopifyGraphqlIdToAdminId(product.id),
        productRef: variant?.sku ?? '',
        productTitle: product.title,
        isPreOrder,
      });

      const authScreenTitle = isSoldOut(product)
        ? t('authentication:dynamicTitle.waitingList')
        : t('authentication:dynamicTitle.product');

      await authenticateAndSubscribe({
        subscriptions: subs,
        authScreenThumbnail: prods.data?.getProducts[0]?.thumbnail?.url,
        authScreenTitle,
        analyticsProperties: {
          'artist name': renderAugmentedArtistsNames(artists),
          'button_location': 'product tile',
          'product_id': shopifyGraphqlIdToAdminId(product.id),
          'product_release_type': product.releaseType,
          'product_status': getProductStatus(product),
          'product_title': product.title,
          'source': isPreOrder
            ? AuthEntryPoint.PreOrder
            : AuthEntryPoint.Product,
        },
        showPhoneStep: phoneStep,
        showOnboarding: !phoneStep,
        hideSuccessScreen: !onboarding?.isOnboardingStarted,
        onFlowComplete: () => {
          onboarding?.show({
            page: AuthEntryPoint.Product,
            id: shopifyGraphqlIdToAdminId(product.id),
          });
        },
      });
      onSubscribe();
    } catch (error) {
      console.error(error);
    }
  };

  const isDrawOrAuction =
    isDrawRelease(product.releaseType) || isAuctionRelease(product.releaseType);

  const drawInFuture =
    (!draw?.preOrderCloseAt || !isValid(new Date(draw?.preOrderCloseAt))) &&
    isFuture(new Date(draw?.openAt || ''));

  const tilePill = getProductTilePill(product, isPreOrder);
  const tileStatus = getProductTileStatus(product, isPreOrder, draw);

  const getActiveClosingDate = (): Date | null => {
    if (!tileStatus?.isActive) return null;

    const rawDateString = (() => {
      if (isDrawOrAuction && draw?.closeAt) return draw.closeAt;
      if (product.closingDate) return product.closingDate;
      return product.launchDate ?? null;
    })();

    if (!rawDateString) return null;

    const date = new Date(rawDateString);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const activeClosingDate = getActiveClosingDate();
  const isDrawClosed = Boolean(
    isDrawOrAuction &&
    activeClosingDate &&
    tileStatus?.isActive &&
    !isFuture(activeClosingDate),
  );

  const showSubscribeBell =
    (isUpcomingProduct && !isDrawOrAuction) ||
    (isDrawOrAuction && drawInFuture);

  const statusTestId = isSoldOut(product)
    ? 'product-tile-status-sold-out'
    : isDrawClosed
      ? 'product-tile-status-draw-closed'
      : 'product-tile-status';

  return (
    <Tile
      className="group"
      direction="column"
      dataTestid={`product-tile-pid-${pid}`}
    >
      <TileMedia
        overlay={
          <>
            {!isSoldOut(product) && tilePill ? (
              <span
                data-testid={
                  tilePill.key === 'tileStatus.draw.upcoming'
                    ? 'product-tile-draw-badge'
                    : undefined
                }
              >
                <Pill isActive={tilePill.isActive} label={t(tilePill.key)} />
              </span>
            ) : null}

            {product.productType === 'Collection' ? (
              <Badge label={product.editionSize.toString()} />
            ) : null}

            {showSubscribeBell ? (
              <SubscribeBell
                isSubscribed={isSubscribed}
                onClickSubscribe={onClickSubscribe}
              />
            ) : null}
          </>
        }
      >
        <Image
          alt={product.alt || product.title}
          eagerLoading={eagerLoading}
          src={product.image}
          hoverEffect
          size={{
            sm: { ratio: '3/4', span: 6 },
            md: { ratio: '3/4', span: 3 },
            lg: { ratio: '3/4', span: 3 },
          }}
        />
      </TileMedia>

      <TileContent className="w-full" direction="column">
        <TileTitle>
          <Link
            href={href}
            locale={false}
            onClick={() => handleClick()}
            prefetch={false}
            className="after:absolute after:inset-0 after:block after:content-['']"
          >
            {renderShopifyArtistName(product.artist)}
          </Link>
        </TileTitle>

        <TileText>{product.title}</TileText>

        <TileText variant="muted">
          <Price draw={draw} product={product} />
        </TileText>

        {tileStatus ? (
          <span className="w-full text-left" data-testid={statusTestId}>
            <TileText
              className="mt-3"
              variant={tileStatus.isActive ? 'active' : 'muted'}
            >
              {tileStatus.variant === 'text' ? (
                t(tileStatus.key)
              ) : (
                <ProductDate
                  date={tileStatus.date}
                  keyCountdownDate={`${tileStatus.key}.date`}
                  keyCountdownDays={`${tileStatus.key}.days`}
                  keyCountdownTime={`${tileStatus.key}.time`}
                  uniqueDateFormat="dd MMMM"
                />
              )}
            </TileText>
          </span>
        ) : null}
      </TileContent>
    </Tile>
  );
};

export default ProductTile;
